import { useState, useEffect } from "react";
import { GetRankingByMonth } from "../services/RankService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faDownload } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { exportMonthlyRankingToExcel } from "../utils/excelExport";

const MonthylRankingTable = ({
  datePostedAtFrom,
  datePostedAtTo,
  topUsers = 100,
}) => {
  const [loading, setLoading] = useState(false);
  const [rankingData, setRankingData] = useState([]);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await GetRankingByMonth(
          datePostedAtFrom,
          datePostedAtTo,
          topUsers,
          page,
        );
        console.log(response);
        if (response.status_code === 200) {
          setRankingData(response.data.ranking || []);
          setTotalPages(response.data.totalPages || 1);
          setTotalUsers(response.data.totalUsers || 0);
        } else {
          setError(response.message || "Có lỗi xảy ra khi lấy dữ liệu");
        }
      } catch (err) {
        setError("Lỗi khi lấy dữ liệu");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (datePostedAtFrom && datePostedAtTo) {
      fetchData();
    }
  }, [datePostedAtFrom, datePostedAtTo, topUsers, page]);

  const handleUser = (user) => {
    // Parse fullName to get firstName and lastName for navigation
    const nameParts = user.fullName.split(" ");
    const lastName = nameParts[0] || "T";
    const firstName = nameParts.slice(1).join(" ") || "T";
    navigate(`/userActivities/${user.id}/${lastName}/${firstName}`);
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      // Transform data to match export format if needed
      const exportData = rankingData.map((user) => ({
        user: {
          id: user.id,
          code: user.code,
          firstName: user.fullName.split(" ").slice(1).join(" ") || "",
          lastName: user.fullName.split(" ")[0] || "",
          phoneNumber: user.phoneNumber,
          totalPostsInCurrentEvent: user.daysPosted,
          totalSales: user.totalSales,
        },
      }));
      await exportMonthlyRankingToExcel(
        exportData,
        datePostedAtFrom,
        datePostedAtTo,
      );
    } catch (error) {
      console.error("Error downloading Excel:", error);
    } finally {
      setDownloading(false);
    }
  };

  if (loading)
    return (
      <div className="text-center text-gray-500 dark:text-gray-300 italic pt-20">
        Đang tải...
      </div>
    );
  if (error)
    return (
      <div className="text-center text-gray-500 dark:text-gray-300 italic pt-20">
        {error}
      </div>
    );
  if (rankingData.length === 0)
    return (
      <div className="pt-20 italic text-lg text-center text-gray-500 dark:text-gray-300">
        Không có dữ liệu xếp hạng.
      </div>
    );

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Bảng xếp hạng tháng
        </h3>
        <button
          onClick={handleDownloadExcel}
          disabled={downloading}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
            downloading
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700 text-white hover:shadow-lg"
          }`}
        >
          {downloading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Đang tải...
            </div>
          ) : (
            <div className="flex items-center">
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Tải Excel
            </div>
          )}
        </button>
      </div>
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Hạng
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              ID
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Mã KH
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Họ tên
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Số điện thoại
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Số ngày hoạt động
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Doanh thu
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Chi tiết
            </th>
          </tr>
        </thead>
        <tbody>
          {rankingData.map((user) => (
            <tr
              key={user.id}
              className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-center">
                {user.rank}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-center">
                {user.id}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                {user.code}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                {user.fullName}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                {user.phoneNumber}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                {user.daysPosted}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                {user.totalSales
                  ? Number(user.totalSales).toLocaleString("vi-VN")
                  : "0"}
              </td>
              <td
                className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center cursor-pointer"
                onClick={() => handleUser(user)}
              >
                <FontAwesomeIcon icon={faCircleInfo} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-center items-center gap-4">
          <button
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page === 1}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              page === 1
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-gray-600 hover:bg-gray-700 text-white"
            }`}
          >
            Trang trước
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Trang {page} / {totalPages} (Tổng: {totalUsers} người)
          </span>
          <button
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={page === totalPages}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              page === totalPages
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-gray-600 hover:bg-gray-700 text-white"
            }`}
          >
            Trang sau
          </button>
        </div>
      )}
    </div>
  );
};

export default MonthylRankingTable;
